import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import {
  useSelector,
} from 'react-redux';

import {
  selectCurrentToken,
} from '../../../features/auth/authSlice';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LoginIcon from '@mui/icons-material/Login';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

import {
  Link as RouterLink,
  useSearchParams,
} from 'react-router-dom';

import { tokens } from '../../../theme';

import Navbar from '../global/Navbar';
import Footer from '../global/Footer';

import {
  useActivatePurchasedAccountMutation,
  useCreatePurchasedAccountSetupMutation,
  useGetCheckoutStatusQuery,
} from '../../../features/orders/ordersApiSlice';

import GoogleCredentialButton from '../../../features/auth/GoogleCredentialButton';

const siteFont = 'Poppins, sans-serif';

const CheckoutSuccess = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const accessToken =
    useSelector(
      selectCurrentToken
    );


  const isLoggedIn =
    Boolean(
      accessToken
    );

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showManualAccountSetup, setShowManualAccountSetup] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');

  const {
    data: checkoutStatus,
    isLoading: isLoadingStatus,
    isError: isStatusError,
    error: statusError,
    refetch: refetchCheckoutStatus,
  } = useGetCheckoutStatusQuery(sessionId, {
    skip: !sessionId,
    pollingInterval: 2000,
    skipPollingIfUnfocused: true,
  });

  const [
    createAccountSetup,
    { isLoading: isCreatingAccountSetup },
  ] = useCreatePurchasedAccountSetupMutation();

  const [
    activatePurchasedAccount,
    { isLoading: isActivatingAccount },
  ] = useActivatePurchasedAccountMutation();

  const isPaid = checkoutStatus?.status === 'paid';
  const accountCreated = Boolean(checkoutStatus?.accountCreated);
  const accountRequested = Boolean(checkoutStatus?.createAccountRequested);
  const accountAuthMethod = checkoutStatus?.accountAuthMethod || 'manual';
  const googleCheckout = accountAuthMethod === 'google';

  const showAccountForm =
    isPaid &&
    !accountCreated &&
    !googleCheckout &&
    (accountRequested || showManualAccountSetup);

  const isCreatingAccount =
    isCreatingAccountSetup || isActivatingAccount;

  const getAccountSetupToken = async () => {
    if (!sessionId) {
      throw new Error('The checkout session could not be identified.');
    }

    const setup = await createAccountSetup(sessionId).unwrap();

    if (!setup?.token) {
      throw new Error('The server did not return an account setup token.');
    }

    return setup.token;
  };

  const handleCreateAccount = async () => {
    setAccountError('');
    setAccountSuccess('');

    if (password.length < 12) {
      setAccountError('Password must contain at least 12 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setAccountError('The passwords do not match.');
      return;
    }

    try {
      const token = await getAccountSetupToken();

      await activatePurchasedAccount({
        token,
        password,
      }).unwrap();

      setPassword('');
      setConfirmPassword('');
      setAccountSuccess('Your USFuel account has been created successfully.');

      await refetchCheckoutStatus();
    } catch (error) {
      console.error('Password account setup failed:', error);

      setAccountError(
        error?.data?.message ||
        error?.message ||
        'Unable to create your account.'
      );
    }
  };

  const handleGoogleCreateAccount = async (googleCredential) => {
    setAccountError('');
    setAccountSuccess('');

    try {
      const token = await getAccountSetupToken();

      await activatePurchasedAccount({
        token,
        googleCredential,
      }).unwrap();

      setPassword('');
      setConfirmPassword('');
      setAccountSuccess(
        'Your USFuel account has been created and linked to Google.'
      );

      await refetchCheckoutStatus();
    } catch (error) {
      console.error('Google account setup failed:', error);

      setAccountError(
        error?.data?.message ||
        error?.message ||
        'Unable to create your account with Google.'
      );
    }
  };

  const renderMainStatus = () => {
    if (!sessionId) {
      return (
        <Alert severity="error">
          We could not identify your checkout session. If you completed payment,
          please contact USFuel support with your purchase email.
        </Alert>
      );
    }

    if (isLoadingStatus) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <CircularProgress sx={{ color: colors.greenAccent[400] }} />

          <Typography
            sx={{
              mt: 2,
              color: 'rgba(255,255,255,0.82)',
              fontWeight: 700,
            }}
          >
            Loading your order...
          </Typography>
        </Box>
      );
    }

    if (isStatusError) {
      return (
        <Alert severity="error">
          {statusError?.data?.message ||
            'We could not retrieve your order status.'}
        </Alert>
      );
    }

    if (checkoutStatus?.status === 'pending') {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py={6}
        >
          <CircularProgress
            size={46}
            sx={{ color: colors.greenAccent[400] }}
          />

          <Typography
            variant="h3"
            sx={{
              mt: 3,
              color: 'white',
              fontWeight: 900,
              letterSpacing: '-0.04em',
            }}
          >
            Confirming your payment
          </Typography>

          <Typography
            sx={{
              mt: 1.5,
              color: 'rgba(255,255,255,0.80)',
              maxWidth: '620px',
              lineHeight: 1.7,
            }}
          >
            Stripe completed your checkout and we're waiting for the payment
            confirmation to reach USFuel. This page will update automatically.
          </Typography>
        </Box>
      );
    }

    if (checkoutStatus?.status === 'payment_failed') {
      return (
        <Alert severity="error">
          The payment could not be confirmed. Please return to checkout and try
          again.
        </Alert>
      );
    }

    if (checkoutStatus?.status === 'canceled') {
      return (
        <Alert severity="warning">
          This checkout session has expired or was canceled.
        </Alert>
      );
    }

    if (!isPaid) {
      return (
        <Alert severity="info">Your order is still being processed.</Alert>
      );
    }

    return (
      <>
        <Box
          sx={{
            width: 82,
            height: 82,
            mx: 'auto',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,255,170,0.12)',
            border: '1px solid rgba(0,255,170,0.30)',
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 52,
              color: colors.greenAccent[400],
            }}
          />
        </Box>

        <Typography
          variant="h2"
          textAlign="center"
          sx={{
            mt: 3,
            color: 'white',
            fontWeight: 900,
            letterSpacing: '-0.055em',
            lineHeight: 1,
          }}
        >
          Order confirmed
        </Typography>

        <Typography
          textAlign="center"
          sx={{
            mt: 1.5,
            mx: 'auto',
            maxWidth: '650px',
            color: 'rgba(255,255,255,0.82)',
            lineHeight: 1.7,
            fontSize: '1rem',
          }}
        >
          Thank you for your purchase. Your payment has been confirmed and your
          USFuel order is now in our system.
        </Typography>

        {checkoutStatus?.email && (
          <Typography
            textAlign="center"
            sx={{
              mt: 1,
              color: 'rgba(255,255,255,0.68)',
            }}
          >
            Order email:{' '}
            <Box
              component="span"
              sx={{
                color: 'white',
                fontWeight: 800,
              }}
            >
              {checkoutStatus.email}
            </Box>
          </Typography>
        )}
      </>
    );
  };

  const renderAccountSection = () => {
    if (!isPaid) {
      return null;
    }


    /*
     * Existing customer who was already
     * authenticated before checkout.
     */
    if (isLoggedIn) {
      return (
        <Alert
          severity="success"
          icon={
            <CheckCircleOutlineIcon />
          }
          sx={{
            mb:
              3,
          }}
        >
          <strong>
            Your order has been added to your USFuel account.
          </strong>{' '}

          You're already signed in, so you can
          view this order and your complete
          order history immediately.
        </Alert>
      );
    }


    /*
     * Account exists now, but user has not
     * authenticated the browser yet.
     *
     * This covers accounts created as part
     * of this checkout.
     */
    if (accountCreated) {
      return (
        <Alert
          severity="success"
          icon={
            <CheckCircleOutlineIcon />
          }
          sx={{
            mb:
              3,
          }}
        >
          <strong>
            Your USFuel account is ready.
          </strong>{' '}

          {googleCheckout
            ? 'Your account is linked to Google and you can now sign into the dashboard.'
            : 'You can now sign into the dashboard using the account created from this purchase.'}
        </Alert>
      );
    }

    if (accountRequested) {
      return (
        <Box>
          <Box display="flex" alignItems="center" gap={1.5} mb={1}>
            <PersonAddAlt1Icon sx={{ color: colors.greenAccent[400] }} />

            <Typography
              variant="h4"
              sx={{
                color: 'white',
                fontWeight: 900,
              }}
            >
              Finish creating your account
            </Typography>
          </Box>

          <Typography
            sx={{
              mb: 3,
              color: 'rgba(255,255,255,0.78)',
              lineHeight: 1.7,
            }}
          >
            Your purchase is confirmed. Continue with the Google account that
            uses {checkoutStatus?.email || 'your purchase email'}, or create a
            password for your USFuel account.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography
          variant="h4"
          sx={{
            color: 'white',
            fontWeight: 900,
            mb: 1,
          }}
        >
          Purchase completed as guest
        </Typography>

        <Typography
          sx={{
            color: 'rgba(255,255,255,0.78)',
            lineHeight: 1.7,
            mb: 2,
          }}
        >
          Your order is complete. You can also create a USFuel account using
          this purchase if you want dashboard access. You can use Google without
          creating a password.
        </Typography>

        {!showManualAccountSetup && (
          <Button
            type="button"
            onClick={() => setShowManualAccountSetup(true)}
            startIcon={<PersonAddAlt1Icon />}
            sx={{
              color: colors.greenAccent[300],
              border: '1px solid rgba(0,255,170,0.28)',
              borderRadius: '999px',
              px: 2.5,
              py: 1,
              fontWeight: 800,
            }}
          >
            Create an account
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box
      width="100%"
      minHeight="100vh"
      sx={{
        background:
          'radial-gradient(circle at 50% 15%, rgba(0,255,170,0.11), transparent 32%)',
      }}
    >
      <Navbar />

      <Container
        maxWidth="md"
        sx={{
          py: {
            xs: 6,
            md: 10,
          },
          fontFamily: siteFont,
          '& .MuiTypography-root': {
            fontFamily: siteFont,
          },
          '& .MuiButton-root': {
            fontFamily: siteFont,
          },
          '& .MuiInputBase-root': {
            fontFamily: siteFont,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 3,
              sm: 4,
              md: 5,
            },
            borderRadius: '28px',
            color: 'white',
            backgroundColor: 'rgba(18,24,32,0.84)',
            backdropFilter: 'blur(18px)',
            border: '1px solid rgba(255,255,255,0.11)',
            boxShadow: '0 30px 90px rgba(0,0,0,0.30)',
          }}
        >
          {renderMainStatus()}

          {isPaid && (
            <>
              <Divider
                sx={{
                  my: 4,
                  borderColor: 'rgba(255,255,255,0.10)',
                }}
              />

              {renderAccountSection()}

              {showAccountForm && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: {
                      xs: 2.5,
                      sm: 3,
                    },
                    borderRadius: '20px',
                    backgroundColor: 'rgba(0,0,0,0.20)',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: 'white',
                      fontWeight: 900,
                      mb: 1,
                    }}
                  >
                    Create your USFuel account
                  </Typography>

                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.76)',
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    Continue with a Google account using{' '}
                    <strong>{checkoutStatus?.email || 'your purchase email'}</strong>,
                    or create a password.
                  </Typography>

                  {accountError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {accountError}
                    </Alert>
                  )}

                  {accountSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {accountSuccess}
                    </Alert>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: 1,
                    }}
                  >
                    <GoogleCredentialButton
                      onCredential={handleGoogleCreateAccount}
                      disabled={isCreatingAccount}
                      text="continue_with"
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      my: 3,
                    }}
                  >
                    <Divider
                      sx={{
                        flex: 1,
                        borderColor: 'rgba(255,255,255,0.14)',
                      }}
                    />

                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.54)',
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      OR CREATE A PASSWORD
                    </Typography>

                    <Divider
                      sx={{
                        flex: 1,
                        borderColor: 'rgba(255,255,255,0.14)',
                      }}
                    />
                  </Box>

                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Password"
                      autoComplete="new-password"
                      value={password}
                      disabled={isCreatingAccount}
                      onChange={(event) => setPassword(event.target.value)}
                      helperText="At least 12 characters"
                      sx={textFieldStyles}
                    />

                    <TextField
                      fullWidth
                      type="password"
                      label="Confirm password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      disabled={isCreatingAccount}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      sx={textFieldStyles}
                    />

                    <Button
                      type="button"
                      onClick={handleCreateAccount}
                      disabled={isCreatingAccount}
                      sx={{
                        alignSelf: {
                          xs: 'stretch',
                          sm: 'flex-start',
                        },
                        minWidth: '250px',
                        backgroundColor: colors.greenAccent[400],
                        color: '#06100c',
                        borderRadius: '999px',
                        py: 1.4,
                        px: 3,
                        fontWeight: 900,
                        '&:hover': {
                          backgroundColor: colors.greenAccent[300],
                        },
                      }}
                    >
                      {isCreatingAccount ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        'Create account with password'
                      )}
                    </Button>
                  </Stack>
                </Paper>
              )}

              <Divider
                sx={{
                  my: 4,
                  borderColor: 'rgba(255,255,255,0.10)',
                }}
              />

              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 900,
                  mb: 2,
                }}
              >
                What's next?
              </Typography>

              <Stack
                direction={{
                  xs:
                    'column',

                  sm:
                    'row',
                }}
                spacing={
                  1.5
                }
              >
                {isLoggedIn ? (
                  <>
                    {/* DASHBOARD */}

                    <Button
                      component={
                        RouterLink
                      }
                      to="/console"
                      startIcon={
                        <DashboardOutlinedIcon />
                      }
                      sx={{
                        flex:
                          1,

                        backgroundColor:
                          colors.greenAccent[400],

                        color:
                          '#06100c',

                        borderRadius:
                          '14px',

                        py:
                          1.5,

                        fontWeight:
                          900,

                        '&:hover': {
                          backgroundColor:
                            colors.greenAccent[300],
                        },
                      }}
                    >
                      Dashboard
                    </Button>


                    {/* ALL ORDERS */}

                    <Button
                      component={
                        RouterLink
                      }
                      to="/orders"
                      startIcon={
                        <ReceiptLongOutlinedIcon />
                      }
                      sx={{
                        flex:
                          1,

                        color:
                          colors.greenAccent[300],

                        border:
                          '1px solid rgba(0,255,170,0.28)',

                        borderRadius:
                          '14px',

                        py:
                          1.5,

                        fontWeight:
                          900,

                        '&:hover': {
                          backgroundColor:
                            'rgba(0,255,170,0.07)',
                        },
                      }}
                    >
                      My Orders
                    </Button>


                    {/* THIS ORDER */}

                    {checkoutStatus
                      ?.orderStatusUrl && (
                        <Button
                          component="a"
                          href={
                            checkoutStatus
                              .orderStatusUrl
                          }
                          startIcon={
                            <LocalShippingOutlinedIcon />
                          }
                          sx={{
                            flex:
                              1,

                            color:
                              'white',

                            border:
                              '1px solid rgba(255,255,255,0.14)',

                            borderRadius:
                              '14px',

                            py:
                              1.5,

                            fontWeight:
                              800,

                            '&:hover': {
                              backgroundColor:
                                'rgba(255,255,255,0.07)',
                            },
                          }}
                        >
                          View This Order
                        </Button>
                      )}
                  </>
                ) : (
                  <>
                    {/* NEWLY-CREATED ACCOUNT */}

                    <Button
                      component={
                        accountCreated
                          ? RouterLink
                          : 'button'
                      }

                      {...(
                        accountCreated
                          ? {
                            to:
                              '/login',
                          }
                          : {}
                      )}

                      disabled={
                        !accountCreated
                      }

                      startIcon={
                        <LoginIcon />
                      }

                      sx={{
                        flex:
                          1,

                        backgroundColor:
                          accountCreated
                            ? colors.greenAccent[400]
                            : 'rgba(255,255,255,0.08)',

                        color:
                          accountCreated
                            ? '#06100c'
                            : 'rgba(255,255,255,0.35)',

                        borderRadius:
                          '14px',

                        py:
                          1.5,

                        fontWeight:
                          900,

                        '&:hover': {
                          backgroundColor:
                            accountCreated
                              ? colors.greenAccent[300]
                              : 'rgba(255,255,255,0.08)',
                        },

                        '&.Mui-disabled': {
                          backgroundColor:
                            'rgba(255,255,255,0.07)',

                          color:
                            'rgba(255,255,255,0.35)',
                        },
                      }}
                    >
                      Sign into dashboard
                    </Button>


                    {/* PUBLIC ORDER LINK */}

                    {checkoutStatus
                      ?.orderStatusUrl && (
                        <Button
                          component="a"
                          href={
                            checkoutStatus
                              .orderStatusUrl
                          }
                          startIcon={
                            <LocalShippingOutlinedIcon />
                          }
                          sx={{
                            flex:
                              1,

                            color:
                              colors.greenAccent[300],

                            border:
                              '1px solid rgba(0,255,170,0.28)',

                            borderRadius:
                              '14px',

                            py:
                              1.5,

                            fontWeight:
                              800,
                          }}
                        >
                          View Order Status
                        </Button>
                      )}
                  </>
                )}
              </Stack>

              {!accountCreated && (
                <Typography
                  sx={{
                    mt: 1.5,
                    textAlign: 'center',
                    color: 'rgba(255,255,255,0.62)',
                    fontSize: '0.84rem',
                  }}
                >
                  {googleCheckout
                    ? 'Your Google account is still being created. Dashboard access will unlock automatically.'
                    : 'Finish creating your USFuel account before signing into the dashboard.'}
                </Typography>
              )}

              <Typography
                sx={{
                  mt: 3,
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.58)',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                }}
              >
                We'll use the information from your order to coordinate
                fulfillment and installation where applicable.
              </Typography>
            </>
          )}
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    color: 'white',

    '& fieldset': {
      borderColor: 'rgba(255,255,255,0.16)',
    },

    '&:hover fieldset': {
      borderColor: 'rgba(255,255,255,0.30)',
    },
  },

  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.66)',
  },

  '& .MuiFormHelperText-root': {
    color: 'rgba(255,255,255,0.52)',
  },
};

export default CheckoutSuccess;